import type { ChangeEvent, HTMLAttributes, ReactNode } from 'react';

import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AlertIcon, FileUploadIcon, FileUploadOutlineIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { formatFileSize } from '../../utils/format-file-size';

import styles from './file-input.module.css';

export type FileInputProps = {
  maximumFileSize?: number;
  acceptedFormats?: string;
  disabled?: boolean;
  icon?: ReactNode;
  heading?: string;
  info?: ReactNode;
  additionalUploadInformation?: string[];
  testIdPrefix?: string;
  inputClassName?: string;
  multiple?: boolean;
  onChange: (files: File[], errors: string[]) => void;
  onError?: (errors: string[]) => void;
} & Omit<HTMLAttributes<HTMLInputElement>, 'onChange'>;

export function FileInput({
  maximumFileSize,
  acceptedFormats,
  disabled,
  icon = <FileUploadOutlineIcon />,
  heading,
  info,
  additionalUploadInformation,
  testIdPrefix,
  className,
  inputClassName,
  children,
  multiple,
  onChange,
  onError,
  ...props
}: FileInputProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const hasErrors = errorMessages.length > 0;

  useEffect(() => {
    if (hasErrors) onError?.(errorMessages);
  }, [hasErrors, errorMessages, onError]);

  const isValidFileExtension = (file: File) => {
    if (!acceptedFormats) return true;

    const acceptedFormatsArray = acceptedFormats.split(',').map((value) => value.replace('.', '').trim().toLowerCase());
    const fileExtension = String(file.name.split('.').pop()).toLowerCase();

    return acceptedFormatsArray.includes(fileExtension);
  };

  const getValidationErrors = (file: File | null) => {
    const errors: string[] = [];

    if (!file) return errors;

    const fileExtensionIsInValid = !isValidFileExtension(file);
    const fileIsTooBig = maximumFileSize !== undefined && maximumFileSize > 0 && file.size > maximumFileSize;

    if (fileExtensionIsInValid) errors.push(t('fileUploader.errors.wrongExtension', { name: file.name }));
    if (fileIsTooBig) errors.push(t('fileUploader.errors.tooBig', { name: file.name }));

    return errors;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const validFiles: File[] = [];
    const errors: string[] = [];

    if (files.length === 0) return;

    // Clear the input so that the same file can be uploaded again
    event.target.value = '';

    files.forEach((file) => {
      const fileErrors = getValidationErrors(file);
      errors.push(...fileErrors);

      if (fileErrors.length === 0) validFiles.push(file);
    });

    setErrorMessages(errors);
    onChange(validFiles, errors);
  };

  const showDragStyles = () => {
    setIsDragging(true);
  };

  const hideDragStyles = () => {
    setIsDragging(false);
  };

  const handleDrop = () => {
    hideDragStyles();
  };

  const defaultFileRestrictions =
    acceptedFormats && maximumFileSize !== undefined
      ? t('fileUploader.defaultFileRestrictions', { formats: acceptedFormats, size: formatFileSize(maximumFileSize) })
      : '';

  const getAdditionalUploadInformation = () => {
    if (additionalUploadInformation === undefined) return defaultFileRestrictions;

    if (additionalUploadInformation?.length > 1) {
      return <ul>{additionalUploadInformation?.map((information, index) => <li key={index}>{information}</li>)}</ul>;
    }

    if (additionalUploadInformation.length === 1) {
      return additionalUploadInformation[0];
    }

    return defaultFileRestrictions;
  };

  return (
    <div
      data-testid={`${testIdPrefix}file-input`}
      className={classNames(styles.border, hasErrors && styles.hasError, className)}
    >
      <div className={styles.wrapper}>
        <input
          data-testid={`${testIdPrefix}file-uploader`}
          type="file"
          role="button"
          className={classNames(styles.input, inputClassName)}
          accept={acceptedFormats}
          onChange={handleChange}
          onDragEnter={showDragStyles}
          onDragLeave={hideDragStyles}
          onDrop={handleDrop}
          disabled={disabled}
          multiple={multiple}
          {...props}
        />

        {!disabled && (
          <div
            className={classNames(styles.dropOverlay, isDragging && styles.isDragging)}
            data-testid={`${testIdPrefix}file-uploader-drop-overlay`}
          >
            <FileUploadIcon />
          </div>
        )}

        {icon}

        {heading && <span className={styles.heading}>{heading}</span>}

        <span className={styles.info}>
          {info || <Trans i18nKey="fileUploader.defaultInfo" components={{ u: <u /> }} />}
        </span>

        <span
          className={styles.additionalUploadInformation}
          data-testid={`${testIdPrefix}additonal-upload-information`}
        >
          {getAdditionalUploadInformation()}
        </span>

        {hasErrors && (
          <span data-testid={`${testIdPrefix}file-uploader-error-message`} className={styles.errorMessages}>
            {errorMessages.map((message, index) => (
              <span key={index} className={styles.errorMessage}>
                {message} <AlertIcon />
              </span>
            ))}
          </span>
        )}

        {!hasErrors && children}
      </div>
    </div>
  );
}
